import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileSpreadsheet, Flame, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import PageHero from "@/components/bakery/PageHero";
import { ShelfArt, ProductIcon } from "@/components/bakery/Art";
import { Loading, OutlineBtn, PrimaryBtn } from "@/components/common/ui";
import { useApi } from "@/lib/useApi";
import { CATEGORY_ORDER, fmtDateLong, groupBy, packInfo } from "@/lib/format";
import { cn } from "@/lib/utils";

const stateOf = (p) => (p.stock < 0 ? "neg" : p.stock === 0 ? "zero" : p.min_stock && p.stock < p.min_stock ? "low" : "ok");
const BADGE = { neg: "MINUS", zero: "HABIS" };

function TodayStats({ t }) {
  const items = [["Diproduksi", t?.produced, "pcs roti jadi", "stat-produced"], ["Frozen Dough", t?.fd_in, "pcs adonan masuk", "stat-fd"],
    ["Terjual", t?.sold, "qty jual hari ini", "stat-sold"], ["Waste", t?.waste, "pcs dibuang", "stat-waste"]];
  return (
    <div className="grid grid-cols-2 border-t-2 border-ink md:grid-cols-4">
      {items.map(([label, v, sub, id], i) => (
        <div key={id} className={cn("px-5 py-4 md:px-8", i % 2 === 1 && "border-l border-zinc-200", i >= 2 && "border-t border-zinc-200 md:border-t-0", i >= 1 && "md:border-l")}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
          <div className={cn("num mt-1 text-3xl font-extrabold", id === "stat-waste" && v > 0 && "text-brand")} data-testid={id}>{v ?? "–"}</div>
          <div className="text-[11px] text-zinc-400">{sub}{id === "stat-produced" && t ? ` · ${t.batches} batch` : ""}</div>
        </div>
      ))}
    </div>
  );
}

function ShelfItem({ p, onPick }) {
  const st = stateOf(p);
  const red = st === "neg" || st === "zero";
  return (
    <button onClick={() => onPick(p)} data-testid={`shelf-item-${p.code}`}
      className={cn("shelf-cell lift press flex flex-col items-start rounded-t-lg bg-white px-4 pb-4 pt-3 text-left", st === "low" && "ring-2 ring-inset ring-brand", red && "bg-[#FDF1F1]")}>
      <div className="flex w-full items-start justify-between">
        <ProductIcon category={p.category} className={cn("h-9 w-9", red ? "text-brand" : "text-ink")} />
        {BADGE[st] && <span className="rounded bg-brand px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-white" data-testid={`shelf-badge-${p.code}`}>{BADGE[st]}</span>}
        {st === "low" && <span className="rounded border border-brand px-1.5 py-0.5 text-[10px] font-bold text-brand">MIN {p.min_stock}</span>}
      </div>
      <div className={cn("num mt-3 text-4xl font-extrabold leading-none tracking-tighter", red && "text-brand")} data-testid={`shelf-stock-${p.code}`}>{p.stock}</div>
      <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">{p.stock_unit}</div>
      {packInfo(p.stock, p) && <div className="text-[11px] leading-tight text-zinc-600">{packInfo(p.stock, p)}</div>}
      <div className="mt-2 line-clamp-2 text-sm font-semibold leading-snug">{p.name}</div>
      <div className="num text-[10px] text-zinc-400">{p.code}</div>
    </button>
  );
}

function Shelf({ category, items, onPick }) {
  return (
    <section className="fade-up mb-14" data-testid={`shelf-${category.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="mb-5 flex items-center gap-3">
        <div className="rounded-md bg-ink px-4 py-2 font-display text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[4px_4px_0_#D62828]">{category}</div>
        <span className="text-xs text-zinc-500">{items.length} produk</span>
        <div className="h-px flex-1 bg-zinc-300" />
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-9 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {items.map((p) => <ShelfItem key={p.id} p={p} onPick={onPick} />)}
      </div>
    </section>
  );
}

function ProductSheet({ p, onClose }) {
  const navigate = useNavigate();
  return (
    <Sheet open={Boolean(p)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="border-l-2 border-ink" data-testid="product-sheet">
        {p && (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-2xl">{p.name}</SheetTitle>
              <SheetDescription>[{p.code}] · {p.category}</SheetDescription>
            </SheetHeader>
            <div className="my-8 text-center">
              <ProductIcon category={p.category} className="mx-auto h-16 w-16" />
              <div className={cn("num mt-3 text-6xl font-extrabold", p.stock <= 0 && "text-brand")}>{p.stock}</div>
              <div className="text-sm text-zinc-500">{p.stock_unit} {packInfo(p.stock, p) ? `· ${packInfo(p.stock, p)}` : ""}</div>
              <div className="mt-2 text-xs text-zinc-400">Minimum stok: {p.min_stock || "-"}</div>
            </div>
            <div className="grid gap-2">
              <PrimaryBtn onClick={() => navigate(`/kartu-stok?product=${p.id}`)} data-testid="sheet-open-kartu"><FileSpreadsheet className="h-4 w-4" /> Lihat Kartu Stok</PrimaryBtn>
              <OutlineBtn onClick={() => navigate("/dapur")} data-testid="sheet-go-dapur"><Flame className="h-4 w-4" /> Catat Produksi</OutlineBtn>
              <OutlineBtn onClick={() => navigate("/waste")} data-testid="sheet-go-waste"><Trash2 className="h-4 w-4" /> Catat Waste</OutlineBtn>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function RakRoti() {
  const { data, loading } = useApi("/stock/summary");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const products = (data?.products || []).filter((p) => !q || `${p.name} ${p.code}`.toLowerCase().includes(q.toLowerCase()));
  const groups = groupBy(products, (p) => p.category);
  const alerts = (data?.products || []).filter((p) => p.category !== "Frozen Dough" && p.stock < 0).length;
  return (
    <div data-testid="rak-roti-page">
      <PageHero testid="rak-hero" eyebrow={`Etalase · ${data ? fmtDateLong(data.date) : ""}`} title="Rak Roti" Art={ShelfArt}
        desc="Stok roti jadi langsung dari Kartu Stok. Merah berarti habis atau minus, garis merah berarti di bawah minimum.">
        <TodayStats t={data?.today} />
      </PageHero>
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari roti, bagel, tortilla…" data-testid="rak-search"
            className="h-12 w-full rounded-md border border-zinc-300 bg-white pl-9 pr-3 text-sm focus:border-ink focus:outline-none" />
        </div>
        {alerts > 0 && <div className="rounded-md bg-brand px-3 py-2 text-xs font-semibold text-white" data-testid="rak-negative-alert">{alerts} produk stok minus — cek Kartu Stok</div>}
      </div>
      {loading && !data ? <Loading /> : CATEGORY_ORDER.filter((c) => groups[c]).map((c) => <Shelf key={c} category={c} items={groups[c]} onPick={setSel} />)}
      <ProductSheet p={sel} onClose={() => setSel(null)} />
    </div>
  );
}
